import SEO from '@/components/SEO';
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import careerHero from "@/assets/career-hero.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState } from "react";
import { Settings, Globe, Lightbulb, Users, Briefcase, MapPin, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Open positions - update this list as new roles open
type OpenPosition = {
  id: string;
  title_el: string;
  title_en: string;
  location_el: string;
  location_en: string;
  type_el: string;
  type_en: string;
  department_el: string;
  department_en: string;
  description_el: string;
  description_en: string;
};

const OPEN_POSITIONS: OpenPosition[] = [
  // Add positions here, e.g.:
  // {
  //   id: "process-engineer-2025",
  //   title_el: "Μηχανικός Process",
  //   title_en: "Process Engineer",
  //   location_el: "Μαρούσι, Αθήνα",
  //   location_en: "Marousi, Athens",
  //   type_el: "Πλήρης απασχόληση",
  //   type_en: "Full-time",
  //   department_el: "Τμήμα Process",
  //   department_en: "Process Department",
  //   description_el: "Σύντομη περιγραφή της θέσης...",
  //   description_en: "Brief description of the role...",
  // },
];

// Validation schema
const careerSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().max(50).optional(),
  area: z.string().optional(),
  message: z.string().trim().max(2000).optional(),
});

export default function CareersPage() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState<null | "success" | "error">(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSending(true);
    setOk(null);

    try {
      const formElement = e.currentTarget;
      const formData = new FormData(formElement);
      
      // Validate text fields
      const data = {
        name: formData.get('fullName') as string,
        email: formData.get('email') as string,
        phone: formData.get('phone') as string,
        area: formData.get('area') as string,
        message: formData.get('message') as string,
      };

      const validatedData = careerSchema.parse({
        fullName: data.name,
        email: data.email,
        phone: data.phone || undefined,
        area: data.area || undefined,
        message: data.message || undefined,
      });

      // Get CV file
      const cvFile = formData.get('cv') as File;
      if (!cvFile || cvFile.size === 0) {
        throw new Error(t('Απαιτείται αρχείο CV', 'CV file is required'));
      }

      // Validate file size (5MB max)
      if (cvFile.size > 5 * 1024 * 1024) {
        throw new Error(t('Το αρχείο CV πρέπει να είναι μικρότερο από 5MB', 'CV file must be less than 5MB'));
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(cvFile.type)) {
        throw new Error(t('Το CV πρέπει να είναι σε μορφή PDF ή DOCX', 'CV must be PDF or DOCX format'));
      }

      // Upload CV to Supabase Storage
      const fileName = `${Date.now()}_${data.name.replace(/\s+/g, '_')}_${cvFile.name}`;
      
      const { supabase } = await import("@/integrations/supabase/client");
      const { error: uploadError } = await supabase.storage
        .from('cvs')
        .upload(fileName, cvFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(t('Σφάλμα κατά το ανέβασμα του CV', 'Error uploading CV'));
      }

      // Send to edge function with file path
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-career-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          area: data.area,
          message: data.message,
          cvPath: fileName,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send application');
      }

      setSending(false);
      setOk("success");
      formElement.reset();
      
      toast({
        title: t("Επιτυχής Αποστολή", "Successful Submission"),
        description: t("Ευχαριστούμε! Θα επικοινωνήσουμε μαζί σας σύντομα.", "Thank you! We'll get back to you soon."),
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSending(false);
      setOk("error");
      
      if (error instanceof z.ZodError) {
        toast({
          title: t("Σφάλμα Επικύρωσης", "Validation Error"),
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else if (error instanceof Error) {
        toast({
          title: t("Σφάλμα", "Error"),
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: t("Σφάλμα", "Error"),
          description: t("Κάτι πήγε στραβά. Προσπαθήστε ξανά.", "Something went wrong. Please try again."),
          variant: "destructive",
        });
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title={t('Καριέρα', 'Careers')}
        description={t('Ευκαιρίες καριέρας στην TFT. Γίνε μέλος μιας δυναμικής ομάδας στη βιομηχανία τροφίμων.', 'Career opportunities at TFT. Join a dynamic team in the food industry.')}
        canonical="https://www.tft.gr/company/careers"
      />
      <Navigation />

      {/* HERO */}
      <div className="w-full">
        <img
          src={careerHero}
          alt={t(
            "Ομάδα μηχανικών σε βιομηχανικό περιβάλλον",
            "Engineering team in industrial environment"
          )}
          className="w-full h-[48vh] md:h-[60vh] object-cover"
          loading="eager"
        />
      </div>

      <main className="flex-1">
        {/* WHY TFT SECTION */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4 max-w-6xl text-center">
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-12"
            >
              {t("Γιατί να επιλέξεις την TFT;", "Why TFT?")}
            </motion.h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
              {[
                {
                  icon: Settings,
                  title: t("Τεχνολογική Υπεροχή", "Technological Excellence"),
                  text: t(
                    "Αξιοποίησε την πολυετή μας εμπειρία σε σύγχρονες τεχνολογίες στο Process, Packaging & Automation και συνέβαλλε στη διαμόρφωση του μέλλοντος της Βιομηχανίας Τροφίμων & Ποτών.",
                    "Leverage our long-standing experience in cutting-edge Process, Packaging & Automation technologies and contribute to shaping the future of the Food & Beverage industry."
                  ),
                },
                {
                  icon: Globe,
                  title: t("Πραγματικός Αντίκτυπος", "Real Impact"),
                  text: t(
                    "Κάθε έργο στο οποίο συμμετέχουμε, μας δίνεται η ευκαιρία να βελτιώσουμε τις γραμμές παραγωγής και την απόδοση της μονάδας, χάρη στη συνεργασία μας με παγκόσμιους προμηθευτές.",
                    "Every project we take on gives us the opportunity to improve production lines and plant performance, thanks to our partnerships with world-class suppliers."
                  ),
                },
                {
                  icon: Lightbulb,
                  title: t("Εκπαίδευση & Εξέλιξη", "Learning & Growth"),
                  text: t(
                    "Απόλαυσε τη δυνατότητα συνεχούς μάθησης, συνεργασία με διεθνείς προμηθευτικούς οίκους και την ανάπτυξη δεξιοτήτων σε ένα σύγχρονο και δυναμικό περιβάλλον, μέσα από πρωτοπόρα και αποδοτικά έργα.",
                    "Enjoy continuous learning, collaboration with international suppliers, and skill development in a modern and dynamic environment, through pioneering and high-impact projects."
                  ),
                },
                {
                  icon: Users,
                  title: t("Ομαδικότητα & Κουλτούρα", "Team & Culture"),
                  text: t(
                    "Εντάξου σε μία πολύπειρη και άρτια εκπαιδευμένη ομάδα, η οποία εκτιμά τη συνεργασία, την ομαδικότητα, την πρωτοβουλία και τη συνέπεια.",
                    "Join an experienced and highly trained team that values collaboration, teamwork, initiative, and consistency."
                  ),
                },
              ].map(({ icon: Icon, title, text }, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    show: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        delay: i * 0.15,
                        when: "beforeChildren",
                        staggerChildren: 0.15,
                      },
                    },
                  }}
                  whileHover={{ y: -4 }}
                  className="p-6 bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow"
                >
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, scale: 0.5, rotate: -15 },
                      show: {
                        opacity: 1,
                        scale: 1,
                        rotate: 0,
                        transition: { type: "spring", stiffness: 200, damping: 12 },
                      },
                    }}
                    className="inline-flex mb-3"
                  >
                    <Icon className="h-8 w-8 text-primary" />
                  </motion.div>
                  <motion.h3
                    variants={{
                      hidden: { opacity: 0, x: -10 },
                      show: { opacity: 1, x: 0, transition: { duration: 0.4 } },
                    }}
                    className="text-xl font-semibold mb-2"
                  >
                    {title}
                  </motion.h3>
                  <motion.p
                    variants={{
                      hidden: { opacity: 0 },
                      show: { opacity: 1, transition: { duration: 0.4 } },
                    }}
                    className="text-muted-foreground text-sm leading-relaxed"
                  >
                    {text}
                  </motion.p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* INTRO */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("Καριέρα", "Careers")}
            </h1>
            <p className="mt-4 text-muted-foreground text-lg">
              {t(
                "Γίνε μέλος μιας ομάδας όπου η τεχνολογική αρτιότητα συναντά τη συνεργασία. Σχεδιάζουμε, ολοκληρώνουμε και υποστηρίζουμε κορυφαία συστήματα Process & Packaging για τη Βιομηχανία Τροφίμων και Ποτών.",
                "Join a team where engineering excellence meets collaboration. We design, integrate and support world-class Process & Packaging systems for the Food & Beverage industry."
              )}
            </p>
          </div>
        </section>

        {/* TABS: OPEN POSITIONS + CV FORM */}
        <section className="pb-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <Tabs defaultValue="cv" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="positions">
                  <Briefcase className="h-4 w-4 mr-2" />
                  {t("Ανοιχτές Θέσεις", "Open Positions")}
                </TabsTrigger>
                <TabsTrigger value="cv">
                  {t("Αποστολή Βιογραφικού", "Send Your CV")}
                </TabsTrigger>
              </TabsList>

              {/* OPEN POSITIONS TAB */}
              <TabsContent value="positions">
                <Card className="border shadow-sm">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="text-2xl font-semibold mb-2">
                      {t("Ανοιχτές Θέσεις Εργασίας", "Open Job Positions")}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6">
                      {t(
                        "Δείτε τις τρέχουσες ανοιχτές θέσεις και υποβάλετε την αίτησή σας.",
                        "Browse our current open roles and submit your application."
                      )}
                    </p>

                    {OPEN_POSITIONS.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
                        <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">
                          {t(
                            "Δεν υπάρχουν ανοιχτές θέσεις αυτή τη στιγμή",
                            "No open positions at the moment"
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                          {t(
                            "Στείλτε μας το βιογραφικό σας και θα επικοινωνήσουμε μαζί σας μόλις προκύψει κατάλληλη ευκαιρία.",
                            "Send us your CV and we'll reach out when a suitable opportunity arises."
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {OPEN_POSITIONS.map((pos) => (
                          <Card key={pos.id} className="border hover:shadow-md transition-shadow">
                            <CardContent className="p-6">
                              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <div>
                                  <h3 className="text-xl font-semibold">
                                    {t(pos.title_el, pos.title_en)}
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {t(pos.department_el, pos.department_en)}
                                  </p>
                                </div>
                                <Badge variant="secondary">
                                  {t(pos.type_el, pos.type_en)}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                                <MapPin className="h-4 w-4" />
                                {t(pos.location_el, pos.location_en)}
                              </div>
                              <p className="text-sm leading-relaxed mb-4">
                                {t(pos.description_el, pos.description_en)}
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* CV FORM TAB */}
              <TabsContent value="cv">
            <Card className="border shadow-sm">
              <CardContent className="p-6 md:p-8">
                <p className="text-base text-foreground/80 mb-6 leading-relaxed border-l-4 border-primary/40 pl-4 italic">
                  {t(
                    "Ακόμα κι αν δεν υπάρχει κάποια ανοιχτή θέση εργασίας που να ταιριάζει με το προφίλ σας, μη διστάσετε να μας στείλετε το βιογραφικό σας με την παρακάτω φόρμα επικοινωνίας.",
                    "Even if there is no open position matching your profile, feel free to send us your CV using the form below."
                  )}
                </p>
                <h2 className="text-2xl font-semibold mb-2">
                  {t("Αποστολή Βιογραφικού", "Send Your CV")}
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {t(
                    "Στείλε μας τα στοιχεία σου και θα επικοινωνήσουμε μαζί σου όταν προκύψει κατάλληλη ευκαιρία.",
                    "Share your details and we will contact you when a suitable opportunity arises."
                  )}
                </p>

                <form
                  onSubmit={onSubmit}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                >
                  {/* Name */}
                  <div className="flex flex-col">
                    <label
                      htmlFor="fullName"
                      className="text-sm font-medium mb-1"
                    >
                      {t("Ονοματεπώνυμο", "Full Name")} *
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      className="h-11 rounded-md border bg-white px-3 outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder={t(
                        "π.χ. Μαρία Παπαδοπούλου",
                        "e.g. Maria Papadopoulou"
                      )}
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col">
                    <label htmlFor="email" className="text-sm font-medium mb-1">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="h-11 rounded-md border bg-white px-3 outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="name@example.com"
                    />
                  </div>

                  {/* Phone (optional) */}
                  <div className="flex flex-col">
                    <label htmlFor="phone" className="text-sm font-medium mb-1">
                      {t("Τηλέφωνο", "Phone")} ({t("προαιρετικό", "optional")})
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      className="h-11 rounded-md border bg-white px-3 outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder="+30 ..."
                    />
                  </div>

                  {/* Area of Interest */}
                  <div className="flex flex-col">
                    <label htmlFor="area" className="text-sm font-medium mb-1">
                      {t("Πεδίο Ενδιαφέροντος", "Area of Interest")}
                    </label>
                    <select
                      id="area"
                      name="area"
                      className="h-11 rounded-md border bg-white px-3 outline-none focus:ring-2 focus:ring-primary/40"
                      defaultValue=""
                    >
                      <option value="" disabled>
                        {t("Επιλέξτε...", "Select...")}
                      </option>
                      <option value="process">
                        {t("Process / Μηχανολογία", "Process / Engineering")}
                      </option>
                      <option value="packaging">
                        {t("Packaging / End-of-Line", "Packaging / End-of-Line")}
                      </option>
                      <option value="automation">
                        {t("Automation / Robotics", "Automation / Robotics")}
                      </option>
                      <option value="lab">
                        {t("Lab / Quality", "Lab / Quality")}
                      </option>
                      <option value="sales">
                        {t("Sales / Project", "Sales / Project")}
                      </option>
                      <option value="service">
                        {t("Service / After-Sales", "Service / After-Sales")}
                      </option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="flex flex-col md:col-span-2">
                    <label
                      htmlFor="message"
                      className="text-sm font-medium mb-1"
                    >
                      {t("Μήνυμα", "Message")} ({t("προαιρετικό", "optional")})
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className="min-h-[120px] rounded-md border bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder={t(
                        "Πείτε μας λίγα λόγια για εσάς…",
                        "Tell us a bit about you…"
                      )}
                    />
                  </div>

                  {/* CV Upload */}
                  <div className="flex flex-col md:col-span-2">
                    <label htmlFor="cv" className="text-sm font-medium mb-1">
                      {t(
                        "Βιογραφικό (PDF ή DOCX, έως 5MB)",
                        "CV (PDF or DOCX, up to 5MB)"
                      )}{" "}
                      *
                    </label>
                    <input
                      id="cv"
                      name="cv"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      required
                      className="rounded-md border bg-white p-2 file:mr-4 file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-primary-foreground hover:file:bg-primary/90"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("Μέγιστο μέγεθος 5MB.", "Maximum size 5MB.")}
                    </p>
                  </div>

                  {/* Submit */}
                  <div className="md:col-span-2 flex items-center gap-3">
                    <Button type="submit" size="lg" disabled={sending}>
                      {sending
                        ? t("Αποστολή…", "Sending…")
                        : t("Αποστολή", "Send")}
                    </Button>
                    {ok === "success" && (
                      <span className="text-sm text-green-600">
                        {t(
                          "Ευχαριστούμε! Θα επικοινωνήσουμε μαζί σας σύντομα.",
                          "Thank you! We'll get back to you soon."
                        )}
                      </span>
                    )}
                    {ok === "error" && (
                      <span className="text-sm text-red-600">
                        {t(
                          "Κάτι πήγε στραβά. Προσπαθήστε ξανά.",
                          "Something went wrong. Please try again."
                        )}
                      </span>
                    )}
                  </div>
                </form>

                <p className="mt-6 text-xs text-muted-foreground">
                  {t(
                    "Με την αποστολή αποδέχεστε την επεξεργασία των δεδομένων σας σύμφωνα με την Πολιτική Απορρήτου.",
                    "By submitting, you agree to our data processing as described in the Privacy Policy."
                  )}
                </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
